import React, {useRef, useState} from "react";
import {useI18next, useTranslation} from "gatsby-plugin-react-i18next";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
import {CaretDown, Database, Plugs, RocketLaunch, Sliders, UsersThree} from "@phosphor-icons/react";
import SectionHeading from "./section-heading";

const Setup = () => {
    const {t} = useTranslation();
    const {language} = useI18next();
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [videoSrc, setVideoSrc] = useState(`/videos/promo_${language}.mp4`);

    const steps = [
        {id: "step-1", icon: Database, title: t("Import your data"), text: t("Add your website, upload files, or connect data sources. ChatLab learns instantly.")},
        {id: "step-2", icon: Sliders, title: t("Customize chatbot"), text: t("Customize colors, logo, position, and behavior to match your brand.")},
        {id: "step-3", icon: RocketLaunch, title: t("Deploy it instantly"), text: t("Paste the code snippet into your website and go live instantly.")},
        {id: "step-4", icon: Plugs, title: t("Connect with your tools"), text: t("Connect with CRMs, e-commerce platforms, or any backend via API.")},
        {id: "step-5", icon: UsersThree, title: t("Connect with your team"), text: t("Transfer conversations to human agents via Live Chat or contact forms.")},
    ];

    const toggle = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play();
            setPlaying(true);
        } else {
            video.pause();
            setPlaying(false);
        }
    };

    return (
        <div id="demo" className="w-full flex flex-col max-w-screen-2xl mx-auto h-full px-5 py-5 gap-10">
            <SectionHeading
                eyebrow={t("From nothing to live")}
                title={t("How it works|in five steps, without a developer.")}
            />
            <div className="flex lg:flex-row flex-col bg-[var(--cl-surface)] border border-[var(--cl-rule)] rounded-[4px] w-full overflow-hidden">
                <div className="w-full lg:w-[34%] p-5 sm:p-6 flex flex-col gap-4 border-b lg:border-b-0 lg:border-r border-[var(--cl-rule)]">
                    <Accordion className="w-full" collapsible type="single" defaultValue="step-1">
                        {steps.map(({id, icon: Icon, title, text}, i) => (
                            <AccordionItem key={id} value={id}>
                                <AccordionTrigger className="text-lg lg:text-xl hover:no-underline py-4 text-left">
                                    <div className="flex gap-3 items-center">
                                        <span className="cl-num text-[13px] text-[var(--cl-muted)] w-5">{String(i + 1).padStart(2, "0")}</span>
                                        <Icon size={22} className="shrink-0 text-[var(--cl-ink)]"/>
                                        {title}
                                    </div>
                                    <CaretDown className="AccordionChevron h-4 w-4 shrink-0 text-[var(--cl-muted)] transition-transform duration-200"/>
                                </AccordionTrigger>
                                <AccordionContent className="text-base text-[var(--cl-ink-2)] pl-8">
                                    {text}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>

                <div className="w-full lg:w-[66%] bg-[var(--cl-surface-2)] flex items-stretch">
                    <div className="relative w-full min-h-[360px] lg:min-h-[560px]">
                        <video
                            ref={videoRef}
                            controls={playing}
                            preload="none"
                            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                            poster="/videos/promoposter.webp"
                            onClick={toggle}
                            playsInline
                            src={videoSrc}
                            onError={() => { if (!videoSrc.endsWith("/promo_en.mp4")) setVideoSrc("/videos/promo_en.mp4"); }}
                        />
                        {!playing ? (
                            <button
                                type="button"
                                className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                                onClick={toggle}
                                aria-label={t("Play the video")}
                            >
                                <span className="flex items-center justify-center w-[72px] h-[72px] rounded-full bg-[var(--cl-ink)] transition-transform duration-200 group-hover:scale-105">
                                    <svg className="w-7 h-7 text-white ms-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                </span>
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Setup;
